<div class="contract-form-container">
  <!-- Loading Spinners -->
  @if (saving || loadingContractData) {
    <div class="loading-overlay">
      <mat-spinner diameter="60"></mat-spinner>
      <p>{{ saving ? 'Saving contract...' : 'Loading contract data...' }}</p>
    </div>
  }

  <mat-sidenav-container class="sidenav-container">
    <!-- Left Actions Sidenav -->
    <mat-sidenav #actionsSidenav mode="side" opened position="start" class="actions-sidenav">
      <app-entity-actions-sidenav
        title="Quick Actions"
        [actions]="sidenavActions">
      </app-entity-actions-sidenav>
    </mat-sidenav>

    <!-- Main Form Content -->
    <mat-sidenav-content class="form-content" [class.with-viewer]="showDocumentViewer">

      <!-- Header Section -->
      <div class="header-section">
        <div class="title-section">
          <h1>{{ isEditMode ? 'Edit Contract' : 'New Contract' }}</h1>
          <p class="subtitle">{{ isEditMode ? 'Update contract details' : 'Create a new contract record' }}</p>
          <p class="required-fields-note">Fields marked with <span class="required-indicator">*</span> are required</p>
        </div>

        <div class="header-actions">
          @if (isEditMode) {
            <button mat-stroked-button (click)="createNewVersion()">
              <mat-icon>content_copy</mat-icon>
              New Version
            </button>
          }
          <button mat-stroked-button color="accent" (click)="cancelSubmit()">
            <mat-icon>close</mat-icon>
            Cancel
          </button>
          <button mat-raised-button color="primary" 
                  [disabled]="!isFormValid() || saving"
                  (click)="onSubmit()">
            <mat-icon>save</mat-icon>
            {{ isEditMode ? 'Update Contract' : 'Save Contract' }}
          </button>
        </div>
      </div>

      <form [formGroup]="form" (ngSubmit)="onSubmit()">
        
        <!-- Hero Section: Contract Name & File Upload -->
        <mat-card class="hero-card">
          <mat-card-content>
            <div class="hero-content">
              <div class="contract-name-section">
                <mat-form-field appearance="outline" class="contract-name-field">
                  <mat-label>Main Document Name</mat-label>
                  <input matInput
                         formControlName="mainDocumentName"
                         placeholder="Filled in when you add the contract file"
                         readonly>
                  <mat-icon matSuffix>attach_file</mat-icon>
                </mat-form-field>
                <mat-form-field appearance="outline" class="contract-name-field">
                  <mat-label>Contract Name <span class="required-indicator">*</span></mat-label>
                  <input matInput 
                         formControlName="nameOfContract" 
                         placeholder="Enter contract name"
                         required>
                  <mat-icon matSuffix>description</mat-icon>
                  @if (form.get('nameOfContract')?.hasError('required')) {
                    <mat-error>
                      Contract name is required
                    </mat-error>
                  }
                </mat-form-field>
              </div>
              
              <div class="upload-section">
                @if (currentDocument) {
                  <!-- File exists - show preview with replace option -->
                  <div class="file-preview-card">
                    <div class="file-info">
                      <mat-icon color="primary" class="file-icon">description</mat-icon>
                      <div class="file-details">
                        <span class="file-name">Contract Document</span>
                        <span class="file-type">{{ currentDocument.fileType === 'pdf' ? 'PDF File' : 'Word Document' }}</span>
                      </div>
                    </div>
                    
                    <div class="file-actions">
                      <button mat-icon-button (click)="removeFile()" type="button" matTooltip="Remove file">
                        <mat-icon>delete</mat-icon>
                      </button>
                      
                      <input type="file" #fileInput
                            (change)="onFileSelected($event)"
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                            style="display: none;">
                      <button mat-stroked-button 
                              type="button"
                              (click)="fileInput.click()">
                        <mat-icon>swap_horiz</mat-icon>
                        Replace File
                      </button>
                    </div>
                  </div>
                } @else {
                  <!-- No file - show upload area -->
                  <div class="upload-area" 
                      [class.drag-over]="isDragOver"
                      (dragover)="onDragOver($event)"
                      (dragleave)="onDragLeave($event)"
                      (drop)="onDrop($event)">
                    <div class="upload-placeholder">
                      <mat-icon class="upload-icon">cloud_upload</mat-icon>
                      <p>Drag & drop contract file here</p>
                      <span>or</span>
                      <input type="file" #fileInputNew
                            (change)="onFileSelected($event)"
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                            style="display: none;">
                      <button mat-stroked-button
                              type="button"
                              (click)="fileInputNew.click()">
                        Browse Files
                      </button>
                      <small>PDF, Word, Excel, or PowerPoint files</small>
                    </div>
                  </div>
                }
                
                <div class="upload-actions">
                  <button mat-raised-button
                          color="accent"
                          type="button"
                          [disabled]="!uploadedFile || aiProcessing"
                          (click)="processWithAI()">
                    @if (aiProcessing) {
                      <mat-spinner diameter="16"></mat-spinner>
                    } @else {
                      <mat-icon>psychology</mat-icon>
                    }
                    {{ aiProcessing ? aiStatusMessage : 'Process with AI' }}
                  </button>

                  <mat-checkbox [(ngModel)]="forceAI" [ngModelOptions]="{standalone: true}" color="primary" class="force-ai-checkbox">
                    Force AI?
                  </mat-checkbox>

                  <button mat-icon-button 
                          type="button"
                          [color]="showDocumentViewer ? 'primary' : ''"
                          [disabled]="!currentDocument"
                          (click)="toggleDocumentViewer()"
                          matTooltip="Toggle contract viewer">
                    <mat-icon>{{ showDocumentViewer ? 'visibility_off' : 'visibility' }}</mat-icon>
                  </button>
                </div>
              </div>
            </div>
          </mat-card-content>
        </mat-card>

        <!-- AI Analysis Results (shown after AI processing completes) -->
        @if (intakeAnalysisResults?.status === 'complete') {
          <mat-card class="ai-analysis-results-card">
            <mat-card-header>
              <mat-card-title>
                <mat-icon class="ai-icon">psychology</mat-icon>
                AI Analysis Results
              </mat-card-title>
              @if (intakeAnalysisResults.analyzedAt) {
                <span class="analysis-timestamp">
                  Analyzed {{ intakeAnalysisResults.analyzedAt | date:'short' }}
                </span>
              }
            </mat-card-header>
            <mat-card-content>

              <!-- Summary Section -->
              @if (aiSummary) {
                <div class="ai-summary-section">
                  <p class="ai-summary-text">{{ aiSummary }}</p>
                </div>
              }

              <!-- Auto-Populated Fields -->
              @if (autoPopulatedFieldsDisplay.length > 0) {
                <div class="auto-populated-section">
                  <h4>
                    <mat-icon>check_circle</mat-icon>
                    Auto-Populated Fields
                  </h4>
                  <div class="auto-populated-list">
                    @for (field of autoPopulatedFieldsDisplay; track field.label) {
                      <div class="auto-populated-item" [class.high-confidence]="field.confidence >= 0.9" [class.medium-confidence]="field.confidence >= 0.7 && field.confidence < 0.9">
                        <span class="field-label">{{ field.label }}:</span>
                        <span class="field-value">{{ field.value }}</span>
                        <span class="confidence-badge" [class.high]="field.confidence >= 0.9" [class.medium]="field.confidence >= 0.7 && field.confidence < 0.9">
                          {{ (field.confidence * 100).toFixed(0) }}%
                        </span>
                      </div>
                    }
                  </div>
                </div>
              }

              <!-- Low Confidence Suggestions -->
              @if (lowConfidenceFields.length > 0) {
                <div class="low-confidence-section">
                  <h4>
                    <mat-icon>info</mat-icon>
                    Suggestions (Review Required)
                  </h4>
                  <div class="suggestions-list">
                    @for (suggestion of lowConfidenceFields; track suggestion.field) {
                      <div class="suggestion-item">
                        <span class="field-label">{{ suggestion.field }}:</span>
                        <span class="field-value">{{ suggestion.suggestion }}</span>
                        <span class="confidence-badge low">{{ (suggestion.confidence * 100).toFixed(0) }}%</span>
                      </div>
                    }
                  </div>
                </div>
              }

              <!-- Key Contract Sections -->
              @if (intakeAnalysisResults.extraction?.keySections) {
                <div class="key-sections-container">
                  <h4>
                    <mat-icon>article</mat-icon>
                    Key Contract Sections
                  </h4>
                  <mat-accordion class="key-sections-accordion">
                    @for (section of getKeySectionsArray(); track section.key) {
                      <mat-expansion-panel [disabled]="!section.found">
                        <mat-expansion-panel-header>
                          <mat-panel-title>
                            @if (section.found) {
                              <mat-icon class="section-found">check_circle</mat-icon>
                            } @else {
                              <mat-icon class="section-not-found">cancel</mat-icon>
                            }
                            {{ section.label }}
                          </mat-panel-title>
                          <mat-panel-description>
                            @if (section.found) {
                              <span class="page-ref">{{ section.pageReference }}</span>
                            } @else {
                              <span class="not-found-text">Not found</span>
                            }
                          </mat-panel-description>
                        </mat-expansion-panel-header>
                        @if (section.found && section.language) {
                          <div class="section-content">
                            <p class="section-language">"{{ section.language }}"</p>
                          </div>
                        }
                      </mat-expansion-panel>
                    }
                  </mat-accordion>
                </div>
              }

              <!-- Party Matching Section -->
              @if (showPartyMatching && intakeAnalysisResults.partyMatches && intakeAnalysisResults.partyMatches.length > 0) {
                <mat-divider class="section-divider"></mat-divider>
                <div class="party-matching-section">
                  <h4>
                    <mat-icon>people</mat-icon>
                    Party Matching
                  </h4>
                  <p class="party-matching-description">
                    Select the matching party from your system for each extracted party, or create a new one.
                  </p>

                  @for (match of intakeAnalysisResults.partyMatches; track match.extractedName) {
                    <div class="party-match-item">
                      <div class="party-match-header">
                        <span class="extracted-name">
                          <strong>{{ match.extractedRole | titlecase }}:</strong>
                          "{{ match.extractedName }}"
                        </span>
                        <span class="match-confidence" [ngClass]="getConfidenceClass(match.matchConfidence)">
                          {{ match.matchConfidence | titlecase }} Match
                        </span>
                      </div>

                      <div class="party-match-options">
                        @if (match.candidates.length > 0) {
                          @for (candidate of match.candidates; track getCandidateId(candidate)) {
                            <label class="party-option">
                              <input type="radio"
                                     [name]="'party-' + match.extractedName"
                                     [value]="getCandidateId(candidate)"
                                     [checked]="isCandidateSelected(match.extractedName, candidate)"
                                     (change)="onPartyMatchSelected(match.extractedName, candidate.entityType, getCandidateId(candidate))">
                              <span class="option-content">
                                <span class="vendor-name">{{ getCandidateName(candidate) }}</span>
                                <span class="vendor-details">
                                  @if (candidate.entityType === 'legalEntity') {
                                    <small class="entity-type-badge">Legal Entity</small>
                                  }
                                  @if (candidate.location) {
                                    <small>{{ candidate.location }}</small>
                                  }
                                  <small class="similarity">({{ (candidate.similarity * 100).toFixed(0) }}% match)</small>
                                </span>
                              </span>
                            </label>
                          }
                        } @else {
                          <div class="no-matches">
                            <mat-icon>warning</mat-icon>
                            <span>No matching parties found in system</span>
                          </div>
                        }

                        <label class="party-option create-new">
                          <input type="radio"
                                 [name]="'party-' + match.extractedName"
                                 [value]="'create-new'"
                                 [checked]="partyMatchCreated.has(match.extractedName)"
                                 (change)="createPartyFromExtracted(match.extractedName)">
                          <span class="option-content">
                            <mat-icon>add_business</mat-icon>
                            <span>Create new party: "{{ match.extractedName }}"</span>
                          </span>
                        </label>

                        <label class="party-option skip-option">
                          <input type="radio"
                                 [name]="'party-' + match.extractedName"
                                 [value]="''"
                                 [checked]="!partyMatchSelections.has(match.extractedName)"
                                 (change)="onPartyMatchSelected(match.extractedName, 'vendor', null)">
                          <span class="option-content">
                            <mat-icon>block</mat-icon>
                            <span>Skip - don't assign this party</span>
                          </span>
                        </label>
                      </div>
                    </div>
                  }

                  <div class="party-matching-actions">
                    <button mat-raised-button color="primary" (click)="applyPartyMatches()">
                      <mat-icon>check</mat-icon>
                      Apply Party Selections
                    </button>
                    <button mat-button (click)="skipPartyMatching()">
                      Skip
                    </button>
                  </div>
                </div>
              }

            </mat-card-content>
          </mat-card>
        }

        <!-- Essential Information Card -->
        <mat-card class="info-card">
          <mat-card-header>
            <mat-card-title>
              <mat-icon>business</mat-icon>
              Essential Information
            </mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <div class="form-grid essential-grid">
              
              <mat-form-field appearance="outline">
                <mat-label>Status</mat-label>
                <mat-select formControlName="status">
                  @for (status of contractStatusTypes; track status) {
                    <mat-option [value]="status">{{ status }}</mat-option>
                  }
                </mat-select>
                <mat-icon matSuffix>flag</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Contract Type</mat-label>
                <mat-select formControlName="contractType">
                  @for (type of contractTypes; track type) {
                    <mat-option [value]="type">{{ type }}</mat-option>
                  }
                </mat-select>
                <mat-icon matSuffix>category</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Legal Entity</mat-label>
                <mat-select formControlName="legalEntities"
                            multiple
                            [compareWith]="compareLegalEntitiesFn">
                  @for (entity of filteredLegalEntities; track entity.legalEntityNum) {
                    <mat-option [value]="entity">
                      {{ entity.legalEntityName }}
                    </mat-option>
                  }
                </mat-select>
                <mat-icon matSuffix>account_balance</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Primary Party <span class="required-indicator">*</span></mat-label>
                <input matInput
                       formControlName="directVendor"
                       [matAutocomplete]="partyAuto"
                       (input)="filterParties($event)"
                       (focus)="resetPartiesFilter()"
                       required>
                <mat-autocomplete #partyAuto="matAutocomplete" [displayWith]="displayPartyFn">
                  @for (party of filteredParties; track party.id) {
                    <mat-option [value]="party">
                      {{ party | partyLabel }}
                    </mat-option>
                  }
                </mat-autocomplete>
                <mat-icon matSuffix>domain</mat-icon>
                @if (form.get('directVendor')?.hasError('required')) {
                  <mat-error>
                    Primary party is required
                  </mat-error>
                }
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Primary Owner</mat-label>
                <input matInput
                       formControlName="primaryOwner"
                       [matAutocomplete]="primaryOwnerAuto"
                       (input)="filterUsers($event)"
                       (focus)="resetUsersFilter()">
                <mat-autocomplete #primaryOwnerAuto="matAutocomplete" [displayWith]="displayUserFn">
                  @for (user of filteredUsers; track user.email) {
                    <mat-option [value]="user">
                      {{ user.friendlyName }}
                    </mat-option>
                  }
                </mat-autocomplete>
                <mat-icon matSuffix>person</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Internal Signatory</mat-label>
                <input matInput
                       formControlName="internalSignatory"
                       [matAutocomplete]="internalSignatoryAuto"
                       (input)="filterUsers($event)"
                       (focus)="resetUsersFilter()">
                <mat-autocomplete #internalSignatoryAuto="matAutocomplete" [displayWith]="displayUserFn">
                  @for (user of filteredUsers; track user.email) {
                    <mat-option [value]="user">
                      {{ user.friendlyName }}
                    </mat-option>
                  }
                </mat-autocomplete>
                <mat-icon matSuffix>edit</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Database Organization</mat-label>
                <mat-select formControlName="databaseOrg">
                  @for (org of databaseOrganizations; track org) {
                    <mat-option [value]="org">{{ org }}</mat-option>
                  }
                </mat-select>
                <mat-icon matSuffix>storage</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Sites</mat-label>
                <mat-select #sitesPicker formControlName="sites" multiple
                            (openedChange)="onPickerOpened($event, 'siteSearchFilter', sitesPicker)">
                  <div class="select-search-container">
                    <input matInput
                           class="select-search-input"
                           placeholder="Search sites..."
                           [(ngModel)]="siteSearchFilter"
                           [ngModelOptions]="{standalone: true}"
                           (keydown)="onSearchKeydown($event)">
                  </div>
                  @for (site of filteredSiteOptions; track site.siteNum) {
                    <mat-option [value]="site">{{ site.name }}</mat-option>
                  }
                </mat-select>
                <mat-icon matSuffix>location_on</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Departments</mat-label>
                <mat-select #departmentsPicker formControlName="departments" multiple
                            (openedChange)="onPickerOpened($event, 'departmentSearchFilter', departmentsPicker)">
                  <div class="select-search-container">
                    <input matInput
                           class="select-search-input"
                           placeholder="Search departments..."
                           [(ngModel)]="departmentSearchFilter"
                           [ngModelOptions]="{standalone: true}"
                           (keydown)="onSearchKeydown($event)">
                  </div>
                  @for (dept of filteredDepartmentOptions; track dept) {
                    <mat-option [value]="dept">{{ dept }}</mat-option>
                  }
                </mat-select>
                <mat-icon matSuffix>business_center</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Regulatory</mat-label>
                <mat-select #regulatoryPicker formControlName="regulatory" multiple
                            (openedChange)="onPickerOpened($event, 'regulatorySearchFilter', regulatoryPicker)">
                  <div class="select-search-container">
                    <input matInput
                           class="select-search-input"
                           placeholder="Search regulatory..."
                           [(ngModel)]="regulatorySearchFilter"
                           [ngModelOptions]="{standalone: true}"
                           (keydown)="onSearchKeydown($event)">
                  </div>
                  @for (regulatory of filteredRegulatoryOptions; track regulatory) {
                    <mat-option [value]="regulatory">{{ regulatory }}</mat-option>
                  }
                </mat-select>
                <mat-icon matSuffix>gavel</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Other Parties</mat-label>
                <mat-select #otherPartiesPicker
                            formControlName="otherParties"
                            multiple
                            [compareWith]="comparePartiesFn"
                            panelClass="wide-select-panel"
                            (openedChange)="onPickerOpened($event, 'otherPartiesSearchFilter', otherPartiesPicker)">
                  <div class="select-search-container">
                    <input matInput
                           class="select-search-input"
                           placeholder="Search parties..."
                           [(ngModel)]="otherPartiesSearchFilter"
                           [ngModelOptions]="{standalone: true}"
                           (keydown)="onSearchKeydown($event)">
                  </div>
                  @for (party of otherPartiesOptions; track party.vendorNum) {
                    <mat-option [value]="party">{{ party | partyLabel }}</mat-option>
                  }
                </mat-select>
                <mat-icon matSuffix>groups</mat-icon>
              </mat-form-field>

            </div>
          </mat-card-content>
        </mat-card>

        <!-- Timeline & Terms Card -->
        <mat-card class="info-card">
          <mat-card-header>
            <mat-card-title>
              <mat-icon>schedule</mat-icon>
              Timeline & Terms
            </mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <div class="form-grid terms-grid">
              
              <mat-form-field appearance="outline">
                <mat-label>Effective Date <span class="required-indicator">*</span></mat-label>
                <input matInput 
                       [matDatepicker]="effectiveDatePicker"
                       formControlName="effectiveDate"
                       required>
                <mat-datepicker-toggle matSuffix [for]="effectiveDatePicker"></mat-datepicker-toggle>
                <mat-datepicker #effectiveDatePicker></mat-datepicker>
                @if (form.get('effectiveDate')?.hasError('required')) {
                  <mat-error>
                    Effective date is required
                  </mat-error>
                }
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Expiration Date <span class="required-indicator">*</span></mat-label>
                <input matInput
                       [matDatepicker]="expirationDatePicker"
                       formControlName="expirationDate"
                       required>
                <mat-datepicker-toggle matSuffix [for]="expirationDatePicker"></mat-datepicker-toggle>
                <mat-datepicker #expirationDatePicker></mat-datepicker>
                @if (form.get('expirationDate')?.hasError('required')) {
                  <mat-error>
                    Expiration date is required
                  </mat-error>
                }
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Last Review Date</mat-label>
                <input matInput
                       [matDatepicker]="lastReviewDatePicker"
                       formControlName="lastReviewDate">
                <mat-datepicker-toggle matSuffix [for]="lastReviewDatePicker"></mat-datepicker-toggle>
                <mat-datepicker #lastReviewDatePicker></mat-datepicker>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Next Review Date</mat-label>
                <input matInput
                       [matDatepicker]="nextReviewDatePicker"
                       formControlName="nextReviewDate">
                <mat-datepicker-toggle matSuffix [for]="nextReviewDatePicker"></mat-datepicker-toggle>
                <mat-datepicker #nextReviewDatePicker></mat-datepicker>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Commencement Date (if applicable)</mat-label>
                <input matInput
                       [matDatepicker]="executionDatePicker"
                       formControlName="executionDate">
                <mat-datepicker-toggle matSuffix [for]="executionDatePicker"></mat-datepicker-toggle>
                <mat-datepicker #executionDatePicker></mat-datepicker>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Internal Execution Date</mat-label>
                <input matInput
                       [matDatepicker]="internalExecutionDatePicker"
                       formControlName="internalExecutionDate">
                <mat-datepicker-toggle matSuffix [for]="internalExecutionDatePicker"></mat-datepicker-toggle>
                <mat-datepicker #internalExecutionDatePicker></mat-datepicker>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Party Execution Date</mat-label>
                <input matInput
                       [matDatepicker]="externalExecutionDatePicker"
                       formControlName="externalExecutionDate">
                <mat-datepicker-toggle matSuffix [for]="externalExecutionDatePicker"></mat-datepicker-toggle>
                <mat-datepicker #externalExecutionDatePicker></mat-datepicker>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Initial Term (months)</mat-label>
                <input matInput 
                       type="number"
                       formControlName="initialTerm">
                <mat-icon matSuffix>event</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Renewal Term (months)</mat-label>
                <input matInput 
                       type="number"
                       formControlName="renewalTerm">
                <mat-icon matSuffix>repeat</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Notice Period (days)</mat-label>
                <input matInput 
                       type="number"
                       formControlName="noticePeriod">
                <mat-icon matSuffix>notification_important</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Review Period (days)</mat-label>
                <input matInput 
                       type="number"
                       formControlName="reviewPeriod">
                <mat-icon matSuffix>rate_review</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Renewal Cycle Limit</mat-label>
                <input matInput 
                       type="number"
                       formControlName="renewalCycleLimit">
                <mat-icon matSuffix>loop</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Number of Renewals</mat-label>
                <input matInput 
                       type="number"
                       formControlName="numRenewals">
                <mat-icon matSuffix>format_list_numbered</mat-icon>
              </mat-form-field>

              <div class="toggle-section">
                <mat-slide-toggle formControlName="autorenew"
                                  (change)="autoRenewToggleChanged($event)">
                  Auto-Renew Contract
                </mat-slide-toggle>
              </div>

              <!-- Term Provision Rich Text Editor -->
              <div class="full-width term-provision-section">
                <label class="term-provision-label">Initial/Amended Term Provision</label>
                <p-editor
                  formControlName="termProvision"
                  [style]="{'height':'250px'}">
                  <ng-template pTemplate="header">
                    <span class="ql-formats">
                      <button class="ql-bold" aria-label="Bold" type="button"></button>
                      <button class="ql-italic" aria-label="Italic" type="button"></button>
                      <button class="ql-underline" aria-label="Underline" type="button"></button>
                    </span>
                  </ng-template>
                </p-editor>
              </div>

            </div>
          </mat-card-content>
        </mat-card>

        <!-- Financial Information Card -->
        <mat-card class="info-card">
          <mat-card-header>
            <mat-card-title>
              <mat-icon>attach_money</mat-icon>
              Financial Information
            </mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <div class="form-grid financial-grid">
              
              <mat-form-field appearance="outline">
                <mat-label>Total Contract Value</mat-label>
                <input matInput 
                       type="number"
                       formControlName="totalValue">
                <span matPrefix>$</span>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Annual Value</mat-label>
                <input matInput 
                       type="number"
                       formControlName="annualValue">
                <span matPrefix>$</span>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Monthly Value</mat-label>
                <input matInput 
                       type="number"
                       formControlName="monthlyValue">
                <span matPrefix>$</span>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Payment Terms</mat-label>
                <input matInput formControlName="paymentTerms">
                <mat-icon matSuffix>payment</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>PO Number</mat-label>
                <input matInput formControlName="poNumber">
                <mat-icon matSuffix>receipt</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Cost Center</mat-label>
                <mat-select formControlName="costCenter">
                  @for (costCenter of costCenters; track costCenter.value) {
                    <mat-option [value]="costCenter.value">
                      {{ costCenter.label }}
                    </mat-option>
                  }
                </mat-select>
                <mat-icon matSuffix>account_balance_wallet</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Purchase Price</mat-label>
                <input matInput
                       type="number"
                       formControlName="purchasePrice">
                <span matPrefix>$</span>
              </mat-form-field>

            </div>
          </mat-card-content>
        </mat-card>

        <!-- People & Relationships Card -->
        <mat-card class="info-card">
          <mat-card-header>
            <mat-card-title>
              <mat-icon>people</mat-icon>
              People & Relationships
            </mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <div class="form-grid people-grid">

              <mat-form-field appearance="outline">
                <mat-label>Secondary Owner</mat-label>
                <input matInput
                       formControlName="secondaryOwner"
                       [matAutocomplete]="secondaryOwnerAuto"
                       (input)="filterUsers($event)"
                       (focus)="resetUsersFilter()">
                <mat-autocomplete #secondaryOwnerAuto="matAutocomplete" [displayWith]="displayUserFn">
                  @for (user of filteredUsers; track user.email) {
                    <mat-option [value]="user">
                      {{ user.friendlyName }}
                    </mat-option>
                  }
                </mat-autocomplete>
                <mat-icon matSuffix>person_outline</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Tertiary Owner</mat-label>
                <input matInput
                       formControlName="tertiaryOwner"
                       [matAutocomplete]="tertiaryOwnerAuto"
                       (input)="filterUsers($event)"
                       (focus)="resetUsersFilter()">
                <mat-autocomplete #tertiaryOwnerAuto="matAutocomplete" [displayWith]="displayUserFn">
                  @for (user of filteredUsers; track user.email) {
                    <mat-option [value]="user">
                      {{ user.friendlyName }}
                    </mat-option>
                  }
                </mat-autocomplete>
                <mat-icon matSuffix>person_outline</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Internal Contract Coordinator</mat-label>
                <input matInput
                       formControlName="yourContractCoordinator"
                       [matAutocomplete]="contractCoordinatorAuto"
                       (input)="filterUsers($event)"
                       (focus)="resetUsersFilter()">
                <mat-autocomplete #contractCoordinatorAuto="matAutocomplete" [displayWith]="displayUserFn">
                  @for (user of filteredUsers; track user.email) {
                    <mat-option [value]="user">
                      {{ user.friendlyName }}
                    </mat-option>
                  }
                </mat-autocomplete>
                <mat-icon matSuffix>account_circle</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Party Signatory</mat-label>
                <input matInput
                       formControlName="partiesSignatory"
                       [matAutocomplete]="partySignatoryAuto"
                       (input)="filterPeople($event)"
                       (focus)="resetPeopleFilter()">
                <mat-autocomplete #partySignatoryAuto="matAutocomplete" [displayWith]="displayPersonFn">
                  @for (person of filteredPeople; track person.contactNum) {
                    <mat-option [value]="person">
                      {{ person.fullName || person.friendlyName }}
                    </mat-option>
                  }
                </mat-autocomplete>
                <mat-icon matSuffix>draw</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Party Contract Coordinator</mat-label>
                <input matInput
                       formControlName="partyContractCoordinator"
                       [matAutocomplete]="partyCoordinatorAuto"
                       (input)="filterPeople($event)"
                       (focus)="resetPeopleFilter()">
                <mat-autocomplete #partyCoordinatorAuto="matAutocomplete" [displayWith]="displayPersonFn">
                  @for (person of filteredPeople; track person.contactNum) {
                    <mat-option [value]="person">
                      {{ person.fullName || person.friendlyName }}
                    </mat-option>
                  }
                </mat-autocomplete>
                <mat-icon matSuffix>person</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Parent Party</mat-label>
                <input matInput
                       [value]="form.get('parentParty')?.value?.name || ''"
                       readonly
                       placeholder="Auto-populated from Primary Party">
                <mat-icon matSuffix>corporate_fare</mat-icon>
                <mat-hint>Automatically set when Primary Party has a parent</mat-hint>
              </mat-form-field>

              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Sub-Contractors</mat-label>
                <mat-select #subcontractorsPicker formControlName="subcontractorVendorNums" multiple
                            panelClass="wide-select-panel"
                            (openedChange)="onPickerOpened($event, 'subcontractorSearchFilter', subcontractorsPicker)">
                  <div class="select-search-container">
                    <input matInput
                           class="select-search-input"
                           placeholder="Search parties..."
                           [(ngModel)]="subcontractorSearchFilter"
                           [ngModelOptions]="{standalone: true}"
                           (keydown)="onSearchKeydown($event)">
                  </div>
                  @for (party of subcontractorOptions; track party.vendorNum) {
                    <mat-option [value]="party.vendorNum">{{ party | partyLabel }}</mat-option>
                  }
                </mat-select>
                <mat-icon matSuffix>engineering</mat-icon>
              </mat-form-field>

            </div>
          </mat-card-content>
        </mat-card>

        <!-- Compliance & Risk Card -->
        <mat-card class="info-card">
          <mat-card-header>
            <mat-card-title>
              <mat-icon>security</mat-icon>
              Compliance & Risk
            </mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <div class="compliance-checkboxes">
              <mat-checkbox formControlName="embeddedBaa">
                Embedded BAA
              </mat-checkbox>
              <mat-checkbox formControlName="baaRequired">
                Missing BAA
              </mat-checkbox>
              <mat-checkbox formControlName="coiRequired">
                Missing COI
              </mat-checkbox>
              <mat-checkbox formControlName="poRequired">
                Missing PO
              </mat-checkbox>
              <mat-checkbox formControlName="missingSignature">
                Missing Signature
              </mat-checkbox>
            </div>

            <div class="form-grid compliance-grid">
              <mat-form-field appearance="outline">
                <mat-label>Risk Category</mat-label>
                <mat-select formControlName="riskCategory">
                  @for (risk of riskCategories; track risk) {
                    <mat-option [value]="risk">{{ risk }}</mat-option>
                  }
                </mat-select>
                <mat-icon matSuffix>warning</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Contract Label</mat-label>
                <mat-select formControlName="contractLabel">
                  @for (label of contractLabels; track label) {
                    <mat-option [value]="label">{{ label }}</mat-option>
                  }
                </mat-select>
                <mat-icon matSuffix>label</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Direct BAA</mat-label>
                <mat-select #baaPicker
                            formControlName="directBaa"
                            [compareWith]="compareBaaFn"
                            panelClass="wide-select-panel"
                            (openedChange)="onPickerOpened($event, 'baaSearchFilter', baaPicker)">
                  <mat-select-trigger>
                    {{ displayBaaName(form.get('directBaa')?.value) }}
                  </mat-select-trigger>
                  <div class="select-search-container">
                    <input matInput
                           class="select-search-input"
                           placeholder="Search BAAs..."
                           [(ngModel)]="baaSearchFilter"
                           [ngModelOptions]="{standalone: true}"
                           (keydown)="onSearchKeydown($event)">
                  </div>
                  <mat-option [value]="null">-- None --</mat-option>
                  @for (baa of filteredBaaOptions; track baa.baaNum) {
                    <mat-option [value]="baa">
                      {{ displayBaaName(baa) }}
                    </mat-option>
                  }
                </mat-select>
                <mat-icon matSuffix>policy</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Direct COI</mat-label>
                <mat-select #coiPicker
                            formControlName="directCoi"
                            [compareWith]="compareCoiFn"
                            panelClass="wide-select-panel"
                            (openedChange)="onPickerOpened($event, 'coiSearchFilter', coiPicker)">
                  <mat-select-trigger>
                    {{ displayCoiName(form.get('directCoi')?.value) }}
                  </mat-select-trigger>
                  <div class="select-search-container">
                    <input matInput
                           class="select-search-input"
                           placeholder="Search COIs..."
                           [(ngModel)]="coiSearchFilter"
                           [ngModelOptions]="{standalone: true}"
                           (keydown)="onSearchKeydown($event)">
                  </div>
                  <mat-option [value]="null">-- None --</mat-option>
                  @for (coi of filteredCoiOptions; track coi.coiNum) {
                    <mat-option [value]="coi">
                      {{ displayCoiName(coi) }}
                    </mat-option>
                  }
                </mat-select>
                <mat-icon matSuffix>verified_user</mat-icon>
              </mat-form-field>
            </div>
          </mat-card-content>
        </mat-card>

        <!-- Additional Details Card (Collapsible) -->
        <mat-card class="info-card">
          <mat-card-header class="expandable-header" (click)="toggleAdditionalDetails()">
            <mat-card-title>
              <mat-icon>more_horiz</mat-icon>
              Additional Details
            </mat-card-title>
            <mat-icon class="expand-icon" [class.expanded]="showAdditionalDetails">
              expand_more
            </mat-icon>
          </mat-card-header>
          
          @if (showAdditionalDetails) {
            <mat-card-content>
              <div class="form-grid additional-grid">
                
                <mat-form-field appearance="outline" class="full-width">
                  <mat-label>Client Contract ID</mat-label>
                  <input matInput formControlName="clientContractId">
                  <mat-icon matSuffix>fingerprint</mat-icon>
                </mat-form-field>

                <mat-form-field appearance="outline" class="full-width">
                  <mat-label>Master Contract</mat-label>
                  <input matInput
                         formControlName="masterContract"
                         [matAutocomplete]="contractAuto"
                         (input)="filterContracts($event)"
                         (focus)="resetContractsFilter()">
                  <mat-autocomplete #contractAuto="matAutocomplete" [displayWith]="displayContractFn">
                    @for (contract of filteredContracts; track contract.id) {
                      <mat-option [value]="contract">
                        {{ contract.nameOfContract }}
                      </mat-option>
                    }
                  </mat-autocomplete>
                  <mat-icon matSuffix>link</mat-icon>
                </mat-form-field>

                <mat-form-field appearance="outline">
                  <mat-label>Renewal Type</mat-label>
                  <mat-select formControlName="renewalType">
                    @for (renewal of renewalTypeLabels; track renewal) {
                      <mat-option [value]="renewal">{{ renewal }}</mat-option>
                    }
                  </mat-select>
                  <mat-icon matSuffix>autorenew</mat-icon>
                </mat-form-field>

                <mat-form-field appearance="outline">
                  <mat-label>Clinical or Administrative</mat-label>
                  <input matInput formControlName="clinicalOrAdmin">
                  <mat-icon matSuffix>local_hospital</mat-icon>
                </mat-form-field>

                <mat-form-field appearance="outline" class="full-width">
                  <mat-label>Description</mat-label>
                  <textarea matInput 
                            formControlName="description"
                            rows="3"
                            placeholder="Contract description and notes">
                  </textarea>
                </mat-form-field>

              </div>
            </mat-card-content>
          }
        </mat-card>


        <!-- Grant Information Card -->
        @if (environment.grants) {
        <mat-card class="info-card">
          <mat-card-header>
            <mat-card-title>
              <mat-icon>account_balance</mat-icon>
              Grant Information
            </mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <div class="form-grid additional-grid">

              <mat-form-field appearance="outline">
                <mat-label>Grant Fund Number</mat-label>
                <input matInput formControlName="grantFundNumber">
                <mat-icon matSuffix>confirmation_number</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Grant Document Number</mat-label>
                <input matInput formControlName="grantDocumentNumber">
                <mat-icon matSuffix>description</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Grant Program Key</mat-label>
                <input matInput formControlName="grantProgramKey">
                <mat-icon matSuffix>vpn_key</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Grant Use</mat-label>
                <input matInput formControlName="grantUse">
                <mat-icon matSuffix>shopping_cart</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Grant Restrictions Type</mat-label>
                <input matInput formControlName="grantRestrictionsType">
                <mat-icon matSuffix>lock</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Grant Program Number</mat-label>
                <input matInput formControlName="grantProgramNumber">
                <mat-icon matSuffix>tag</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Revised Award Value</mat-label>
                <input matInput formControlName="revisedAwardValue" type="number">
                <mat-icon matSuffix>attach_money</mat-icon>
              </mat-form-field>

            </div>
          </mat-card-content>
        </mat-card>
        }

        <!-- Value Calculations Card -->
        @if (environment.value) {
        <mat-card class="info-card">
          <mat-card-header>
            <mat-card-title>
              <mat-icon>calculate</mat-icon>
              Value Calculations
            </mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <div class="form-grid additional-grid">

              <mat-form-field appearance="outline">
                <mat-label>Value per Patient</mat-label>
                <input matInput formControlName="valuePatient" type="number">
                <mat-icon matSuffix>attach_money</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Value per Hour</mat-label>
                <input matInput formControlName="valueHour" type="number">
                <mat-icon matSuffix>attach_money</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Value per Day</mat-label>
                <input matInput formControlName="valueDay" type="number">
                <mat-icon matSuffix>attach_money</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Value per Month</mat-label>
                <input matInput formControlName="valueMonth" type="number">
                <mat-icon matSuffix>attach_money</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Value per Resident Per Year</mat-label>
                <input matInput formControlName="valueResidentPerYear" type="number">
                <mat-icon matSuffix>attach_money</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Value per Diem</mat-label>
                <input matInput formControlName="valuePerDiem" type="number">
                <mat-icon matSuffix>attach_money</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Value per Encounter</mat-label>
                <input matInput formControlName="valueEncounter" type="number">
                <mat-icon matSuffix>attach_money</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Value per Delivery</mat-label>
                <input matInput formControlName="valueDelivery" type="number">
                <mat-icon matSuffix>attach_money</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Value per Workshop</mat-label>
                <input matInput formControlName="valueWorkshop" type="number">
                <mat-icon matSuffix>attach_money</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Value per Clinic</mat-label>
                <input matInput formControlName="valueClinic" type="number">
                <mat-icon matSuffix>attach_money</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Value per Class</mat-label>
                <input matInput formControlName="valueClass" type="number">
                <mat-icon matSuffix>attach_money</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Value per Event</mat-label>
                <input matInput formControlName="valueEvent" type="number">
                <mat-icon matSuffix>attach_money</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Value per Program</mat-label>
                <input matInput formControlName="valueProgram" type="number">
                <mat-icon matSuffix>attach_money</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Value per Student Per Week</mat-label>
                <input matInput formControlName="valueStudentPerWeek" type="number">
                <mat-icon matSuffix>attach_money</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Value per Student Per Month</mat-label>
                <input matInput formControlName="valueStudentPerMonth" type="number">
                <mat-icon matSuffix>attach_money</mat-icon>
              </mat-form-field>

            </div>
          </mat-card-content>
        </mat-card>
        }

        <!-- SOCHS/AP Compensation Card -->
        @if (environment.sochsAccordion) {
        <mat-card class="info-card">
          <mat-card-header>
            <mat-card-title>
              <mat-icon>payments</mat-icon>
              SOCHS/AP Compensation
            </mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <div class="form-grid additional-grid">

              <mat-form-field appearance="outline">
                <mat-label>AP Compensation Structure</mat-label>
                <input matInput formControlName="apCompensationStructure">
                <mat-icon matSuffix>account_tree</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Acute Care Compensation</mat-label>
                <input matInput formControlName="acuteCareComp" type="number">
                <mat-icon matSuffix>attach_money</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>AP Sign-On Bonus Amount</mat-label>
                <input matInput formControlName="apSignOnBonusAmount" type="number">
                <mat-icon matSuffix>attach_money</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>AP Retention Bonus</mat-label>
                <input matInput formControlName="apRetentionBonus" type="number">
                <mat-icon matSuffix>attach_money</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>AP Base Salary</mat-label>
                <input matInput formControlName="apBaseSalary" type="number">
                <mat-icon matSuffix>attach_money</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>AP Hourly Rate</mat-label>
                <input matInput formControlName="apHourlyRate" type="number">
                <mat-icon matSuffix>attach_money</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>AP Productivity Bonus</mat-label>
                <input matInput formControlName="apProductivityBonus" type="number">
                <mat-icon matSuffix>attach_money</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>AP Quality Bonus</mat-label>
                <input matInput formControlName="apQualityBonus" type="number">
                <mat-icon matSuffix>attach_money</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>AP Shift Differential</mat-label>
                <input matInput formControlName="apShiftDifferential" type="number">
                <mat-icon matSuffix>attach_money</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>AP Call Pay</mat-label>
                <input matInput formControlName="apCallPay" type="number">
                <mat-icon matSuffix>attach_money</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>AP Malpractice Coverage</mat-label>
                <input matInput formControlName="apMalpracticeCoverage">
                <mat-icon matSuffix>verified_user</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>AP Relocation Assistance</mat-label>
                <input matInput formControlName="apRelocationAssistance" type="number">
                <mat-icon matSuffix>attach_money</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>AP Student Loan Repayment</mat-label>
                <input matInput formControlName="apStudentLoanRepayment" type="number">
                <mat-icon matSuffix>attach_money</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>AP Tail Coverage</mat-label>
                <input matInput formControlName="apTailCoverage">
                <mat-icon matSuffix>verified_user</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>AP Annual Bonus Potential</mat-label>
                <input matInput formControlName="apAnnualBonusPotential" type="number">
                <mat-icon matSuffix>attach_money</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>AP Stipend</mat-label>
                <input matInput formControlName="apStipend" type="number">
                <mat-icon matSuffix>attach_money</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>AP RVU Rate</mat-label>
                <input matInput formControlName="apRvuRate">
                <mat-icon matSuffix>show_chart</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>AP Minimum Guarantee</mat-label>
                <input matInput formControlName="apMinimumGuarantee" type="number">
                <mat-icon matSuffix>attach_money</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>AP Benefits Package Value</mat-label>
                <input matInput formControlName="apBenefitsPackageValue" type="number">
                <mat-icon matSuffix>attach_money</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>AP Vacation Days</mat-label>
                <input matInput formControlName="apVacationDays">
                <mat-icon matSuffix>beach_access</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>AP CME Allowance</mat-label>
                <input matInput formControlName="apCmeAllowance" type="number">
                <mat-icon matSuffix>attach_money</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>AP Partnership Track</mat-label>
                <input matInput formControlName="apPartnershipTrack">
                <mat-icon matSuffix>handshake</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>AP Non-Compete Radius</mat-label>
                <input matInput formControlName="apNonCompeteRadius">
                <mat-icon matSuffix>location_on</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>AP Non-Compete Duration</mat-label>
                <input matInput formControlName="apNonCompeteDuration">
                <mat-icon matSuffix>schedule</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>AP Work Schedule</mat-label>
                <input matInput formControlName="apWorkSchedule">
                <mat-icon matSuffix>event</mat-icon>
              </mat-form-field>

            </div>
          </mat-card-content>
        </mat-card>
        }

        <!-- HR Details Card -->
        @if (environment.hrAccordion) {
        <mat-card class="info-card">
          <mat-card-header>
            <mat-card-title>
              <mat-icon>work</mat-icon>
              HR Details
            </mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <div class="form-grid additional-grid">

              <mat-form-field appearance="outline">
                <mat-label>HR CME Dues</mat-label>
                <input matInput formControlName="hrCmeDues" type="number">
                <mat-icon matSuffix>attach_money</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>HR PTO Time Off</mat-label>
                <input matInput formControlName="hrPtoTimeOff">
                <mat-icon matSuffix>calendar_today</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Acute Care Specialty</mat-label>
                <input matInput formControlName="acuteCareSpecialty">
                <mat-icon matSuffix>local_hospital</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>HR Group</mat-label>
                <input matInput formControlName="hrGroup">
                <mat-icon matSuffix>group</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>HR Product</mat-label>
                <input matInput formControlName="hrProduct">
                <mat-icon matSuffix>inventory_2</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>HR Lawson Number</mat-label>
                <input matInput formControlName="hrLawsonNumber">
                <mat-icon matSuffix>confirmation_number</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>HR Position Title</mat-label>
                <input matInput formControlName="hrPositionTitle">
                <mat-icon matSuffix>badge</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>HR Department Code</mat-label>
                <input matInput formControlName="hrDepartmentCode">
                <mat-icon matSuffix>tag</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>HR Job Code</mat-label>
                <input matInput formControlName="hrJobCode">
                <mat-icon matSuffix>tag</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>HR FTE</mat-label>
                <input matInput formControlName="hrFte">
                <mat-icon matSuffix>percent</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>HR Supervisor Name</mat-label>
                <input matInput formControlName="hrSupervisorName">
                <mat-icon matSuffix>person</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>HR Employee ID</mat-label>
                <input matInput formControlName="hrEmployeeId">
                <mat-icon matSuffix>badge</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>HR Pay Grade</mat-label>
                <input matInput formControlName="hrPayGrade">
                <mat-icon matSuffix>trending_up</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>HR Pay Type</mat-label>
                <input matInput formControlName="hrPayType">
                <mat-icon matSuffix>payment</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>HR Benefits Eligible</mat-label>
                <input matInput formControlName="hrBenefitsEligible">
                <mat-icon matSuffix>check_circle</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>HR Start Date</mat-label>
                <input matInput formControlName="hrStartDate" [matDatepicker]="hrStartPicker">
                <mat-datepicker-toggle matSuffix [for]="hrStartPicker"></mat-datepicker-toggle>
                <mat-datepicker #hrStartPicker></mat-datepicker>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>HR End Date</mat-label>
                <input matInput formControlName="hrEndDate" [matDatepicker]="hrEndPicker">
                <mat-datepicker-toggle matSuffix [for]="hrEndPicker"></mat-datepicker-toggle>
                <mat-datepicker #hrEndPicker></mat-datepicker>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>HR Location Code</mat-label>
                <input matInput formControlName="hrLocationCode">
                <mat-icon matSuffix>location_on</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>HR Cost Center</mat-label>
                <input matInput formControlName="hrCostCenter">
                <mat-icon matSuffix>account_balance</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>HR Union Status</mat-label>
                <input matInput formControlName="hrUnionStatus">
                <mat-icon matSuffix>groups</mat-icon>
              </mat-form-field>

            </div>
          </mat-card-content>
        </mat-card>
        }

        <!-- Lease Management Card -->
        @if (environment.leaseManagement) {
        <mat-card class="info-card">
          <mat-card-header>
            <mat-card-title>
              <mat-icon>home_work</mat-icon>
              Lease Management
            </mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <div class="form-grid additional-grid">

              <mat-form-field appearance="outline">
                <mat-label>Lease Start Date</mat-label>
                <input matInput formControlName="leaseStartDate" [matDatepicker]="leaseStartPicker">
                <mat-datepicker-toggle matSuffix [for]="leaseStartPicker"></mat-datepicker-toggle>
                <mat-datepicker #leaseStartPicker></mat-datepicker>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Lease End Date</mat-label>
                <input matInput formControlName="leaseEndDate" [matDatepicker]="leaseEndPicker">
                <mat-datepicker-toggle matSuffix [for]="leaseEndPicker"></mat-datepicker-toggle>
                <mat-datepicker #leaseEndPicker></mat-datepicker>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Lease Monthly Rent</mat-label>
                <input matInput formControlName="leaseMonthlyRent" type="number">
                <mat-icon matSuffix>attach_money</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Lease Annual Rent</mat-label>
                <input matInput formControlName="leaseAnnualRent" type="number">
                <mat-icon matSuffix>attach_money</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Lease Square Footage</mat-label>
                <input matInput formControlName="leaseSquareFootage">
                <mat-icon matSuffix>square_foot</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Lease Price Per Square Foot</mat-label>
                <input matInput formControlName="leasePricePerSqFt" type="number">
                <mat-icon matSuffix>attach_money</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Lease Security Deposit</mat-label>
                <input matInput formControlName="leaseSecurityDeposit" type="number">
                <mat-icon matSuffix>attach_money</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Lease Escalation Clause</mat-label>
                <input matInput formControlName="leaseEscalationClause">
                <mat-icon matSuffix>trending_up</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Lease Escalation Percentage</mat-label>
                <input matInput formControlName="leaseEscalationPercentage">
                <mat-icon matSuffix>percent</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Lease Renewal Options</mat-label>
                <input matInput formControlName="leaseRenewalOptions">
                <mat-icon matSuffix>autorenew</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Lease Property Address</mat-label>
                <input matInput formControlName="leasePropertyAddress">
                <mat-icon matSuffix>location_on</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Lease Type</mat-label>
                <input matInput formControlName="leaseType">
                <mat-icon matSuffix>category</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Lease Landlord Name</mat-label>
                <input matInput formControlName="leaseLandlordName">
                <mat-icon matSuffix>person</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Lease Operating Expenses</mat-label>
                <input matInput formControlName="leaseOperatingExpenses" type="number">
                <mat-icon matSuffix>attach_money</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Lease Parking Spaces</mat-label>
                <input matInput formControlName="leaseParkingSpaces">
                <mat-icon matSuffix>local_parking</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Lease Early Termination Clause</mat-label>
                <input matInput formControlName="leaseEarlyTerminationClause">
                <mat-icon matSuffix>cancel</mat-icon>
              </mat-form-field>

            </div>
          </mat-card-content>
        </mat-card>
        }

        <!-- Validation Message -->
        @if (!isFormValid()) {
          <mat-card class="validation-card">
            <mat-card-content>
              <div class="validation-message">
                <mat-icon color="warn">warning</mat-icon>
                <span>{{ getValidationMessage() }}</span>
              </div>
            </mat-card-content>
          </mat-card>
        }

      </form>

    </mat-sidenav-content>

    <!-- Contract Viewer Sidenav -->
    <mat-sidenav mode="side" 
                position="end"
                [opened]="showDocumentViewer"
                class="contract-viewer">
      <app-document-viewer-sidenav
        [document]="currentDocument"
        [title]="'Contract Viewer'"
        (close)="toggleDocumentViewer()"
        (download)="downloadDocument()"
        (openNewTab)="openDocumentInNewTab()">
      </app-document-viewer-sidenav>
    </mat-sidenav>


  </mat-sidenav-container>
</div>